'use client';

import { Close } from '@radix-ui/react-dialog';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/app_components/common/Icon';
import { BasicModal } from '@/app_components/ui/BasicModal';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import {
  VideoBase,
  VideoContent as VideoContentLayout,
  VideoDate,
  VideoDescription,
  VideoPlayer,
  VideoTitle,
} from '@/app_components/Video/Video';
import { VideoDefault } from '@/app_components/Video/VideoDefault';
import { useFirstClickedVideo } from '@/app_lib/hooks/useFirstClickedVideo';
import { useOverrideStnPlayerStyles } from '@/app_lib/hooks/useOverrideStnPlayerStyles';
import type { Video } from '@/app_lib/types/Video';

const VideoContent = ({
  target,
  videos,
  currentVideoIndex,
  onNextVideo,
  initialVideoId: initialVideoIdProp,
}: {
  target: string;
  videos: Video[];
  currentVideoIndex: number;
  onNextVideo: () => void;
  initialVideoId?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VoltaxPlayer | null>(null);
  const onNextVideoRef = useRef(onNextVideo);

  // When exactly one player already exists on the page (e.g. the hero),
  // append a `dedupKey` to the Script src so Next.js creates a second
  // player instead of deduplicating. When two players already exist the
  // modal can reuse the second one, so no dedupKey is needed.
  const [{ stnVideoId, dedupKey }] = useState(() => {
    const preferredId = initialVideoIdProp ?? videos[0].id;
    if (typeof document === 'undefined') {
      return { dedupKey: undefined as string | undefined, stnVideoId: preferredId };
    }

    const playerCount = window.voltax?.getAllPlayersOnPage()?.length ?? 0;
    if (playerCount === 1) {
      return { dedupKey: target, stnVideoId: preferredId };
    }

    return { dedupKey: undefined as string | undefined, stnVideoId: preferredId };
  });
  onNextVideoRef.current = onNextVideo;

  // Use replacePlayerContent() when currentVideo changes
  useEffect(() => {
    const currentVideoId = videos[currentVideoIndex].id;
    if (!playerRef.current) {
      return;
    }

    if (playerRef.current) {
      playerRef.current.replacePlayerContent({ playerKey: currentVideoId });
    }
  }, [currentVideoIndex, videos]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
  }, [currentVideoIndex]);

  useOverrideStnPlayerStyles(target);

  // Set up Voltax player listeners once (player is stable, never re-created)
  // biome-ignore lint/correctness/useExhaustiveDependencies: player setup references stable refs; currentVideoIndex triggers content replacement
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isAdvancing = false;
    let isFirstVideoStart = true;
    let listenedVideoEl: HTMLVideoElement | null = null;

    const onTimeUpdate = () => {
      if (!listenedVideoEl) {
        return;
      }

      const secondsBeforeVideoFinishes = 0.7;
      const remaining = listenedVideoEl.duration - listenedVideoEl.currentTime;
      if (!isAdvancing && remaining <= secondsBeforeVideoFinishes) {
        isAdvancing = true;
        onNextVideoRef.current();
      }
    };

    const setupTimeUpdate = () => {
      const voltaxPlayer = container.querySelector('voltax-player');
      const videoEl = voltaxPlayer?.shadowRoot?.querySelector('video');

      if (!videoEl || videoEl === listenedVideoEl) {
        return;
      }

      if (listenedVideoEl) {
        listenedVideoEl.removeEventListener('timeupdate', onTimeUpdate);
      }

      listenedVideoEl = videoEl;
      videoEl.addEventListener('timeupdate', onTimeUpdate);
    };

    const onVideoStart = () => {
      isAdvancing = false;
      setupTimeUpdate();

      const voltaxPlayer = container.querySelector('voltax-player');
      const shadowRoot = voltaxPlayer?.shadowRoot;
      if (!shadowRoot) {
        return;
      }

      // Interact with video to avoid stay/next prompt
      const playBtn = shadowRoot.querySelector<HTMLButtonElement>('.voltax-mp-play-button');
      playBtn?.click();
      playBtn?.click();

      if (isFirstVideoStart) {
        isFirstVideoStart = false;
        const videoEl = shadowRoot.querySelector('video');
        const muteBtn = shadowRoot.querySelector<HTMLButtonElement>('.voltax-mp-mute-button');
        if (videoEl?.muted) {
          muteBtn?.click();
        }
      }
    };

    const registerListeners = (player: VoltaxPlayer) => {
      playerRef.current = player;
      player.replacePlayerContent({ playerKey: videos[currentVideoIndex].id });
      player.on('videoStart', onVideoStart);
    };

    const setupListeners = () => {
      const players = window.voltax?.getAllPlayersOnPage() ?? [];
      if (players.length > 1) {
        registerListeners(players[1]);
      }
    };

    if (window.voltax) {
      setupListeners();
    }

    const onPlayerLoaded = () => setupListeners();
    window.addEventListener('voltaxPlayerLoaded', onPlayerLoaded);

    return () => {
      window.removeEventListener('voltaxPlayerLoaded', onPlayerLoaded);
      if (listenedVideoEl) {
        listenedVideoEl.removeEventListener('timeupdate', onTimeUpdate);
      }
    };
  }, [currentVideoIndex]);

  const activeVideo = videos[currentVideoIndex];

  return (
    <div
      className='bg-black w-screen h-[95dvh] rounded-t-lg pb-default overflow-hidden'
      data-theme='dark'
      ref={containerRef}
    >
      <div className='text-primary layout-safe-area w-full flex flex-col h-full min-h-0'>
        <div className='flex justify-end w-full py-sm shrink-0'>
          <Close asChild>
            <ButtonV2
              as='button'
              isTertiary
              variant='secondary'
            >
              <span className='hidden md:inline'>Close</span>
              <Icon icon='X' />
            </ButtonV2>
          </Close>
        </div>
        <div
          className='flex flex-col lg:flex-row layout-grid-gap justify-start lg:justify-center overflow-y-auto lg:overflow-hidden min-h-0'
          ref={scrollContainerRef}
        >
          <VideoBase
            className='max-lg:flex-none'
            modal
            orientation='vertical'
          >
            <VideoPlayer
              dedupKey={dedupKey}
              layout='default'
              modal
              orientation='vertical'
              target={target}
              videoId={stnVideoId}
            />
            <VideoContentLayout
              modal
              orientation='vertical'
            >
              <VideoTitle
                className='typography-level-6'
                modal
                orientation='vertical'
                playing
                title={activeVideo.title}
              />
              <VideoDescription
                description={activeVideo.summary}
                modal
                orientation='vertical'
              />
              <VideoDate
                date={activeVideo.age}
                modal
                orientation='vertical'
              />
            </VideoContentLayout>
          </VideoBase>
          <div className='flex flex-col layout-gap-xs w-full lg:w-[368px] lg:overflow-y-auto lg:min-h-0 lg:flex-none'>
            {videos.map((video, index) => (
              <VideoDefault
                data-index={index}
                key={video.videoUrl}
                modal
                orientation='horizontal'
                target={target}
                video={video}
                watching={currentVideoIndex === index}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// NOTE: The state is built into the component, not the content component
// This allows us to preserve the state when the modal is closed and reopened
export const VideoModal = ({ target, videos }: { target: string; videos: Video[] }) => {
  const [open, setOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const { firstClickedVideoId, setFirstClickedVideoId } = useFirstClickedVideo();

  const onNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
  };

  const content = (
    <VideoContent
      currentVideoIndex={currentVideoIndex}
      initialVideoId={firstClickedVideoId ?? undefined}
      onNextVideo={onNextVideo}
      target={target}
      videos={videos}
    />
  );

  // Mute the hero player when the modal opens so audio doesn't overlap
  useEffect(() => {
    if (!open) {
      return;
    }

    const heroContainer = document.querySelector('[class*="video-player-hero"]');
    const shadowRoot = heroContainer?.querySelector('voltax-player')?.shadowRoot;
    const videoEl = shadowRoot?.querySelector('video');
    const muteBtn = shadowRoot?.querySelector<HTMLButtonElement>('.voltax-mp-mute-button');

    if (videoEl && !videoEl.muted && muteBtn) {
      muteBtn.click();
    }
  }, [open]);

  // This use effect is used to handle the click event on the video thumbnails
  // It works both when the modal is open and closed, allowing us to select a video from the carousel or in the modal
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const el = (event.target as HTMLElement)?.closest(`.${target}`) as HTMLElement | null;

      if (!el) {
        return;
      }

      // get the data-index of the clicked video
      const index = el.getAttribute('data-index');
      const parsedIndex = index ? parseInt(index) : 0;

      setFirstClickedVideoId(videos[parsedIndex].id);

      if (!open) {
        setOpen(true);
      }

      if (parsedIndex !== currentVideoIndex) {
        setCurrentVideoIndex(parsedIndex);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [currentVideoIndex, open, target, videos, setFirstClickedVideoId]);

  return (
    <BasicModal
      content={content}
      onOpenChange={setOpen}
      openOverride={open}
      position='bottom'
    />
  );
};
